import { formatDate } from '../../shared/formatDate';
import { text } from '../../shared/text';
import { formatMeasurement } from './measurements';
import type { Measurement, MeasurementKind } from './measurements';

/**
 * A measurement over time, drawn as the plainest thing that is still a line.
 *
 * ## It is specified by what it must not have
 *
 * A chart is the single most likely place in this product for `client-tone` to
 * be broken by accident, because every charting library anybody would reach for
 * ships targets, goal lines, trend lines and traffic-light colours as *defaults*
 * (`client-me` design, D2). So this is hand-drawn SVG, in about forty lines, and
 * none of those things exists to be switched off:
 *
 *   * **no target, no goal line, no ideal band, no reference value.** There is
 *     no prop for one and nothing in the data model that could supply one — a
 *     coach cannot set a number a client is meant to reach;
 *   * **no score, no grade, no percentage, no streak**;
 *   * **no comparison with any other person or any population.** The chart
 *     receives one client's own numbers and nothing else;
 *   * **no trend line and no projection.** The line joins the points that exist
 *     and stops;
 *   * **one ink, always.** The stroke is `currentColor` and the wrapper sets
 *     `text-primary` unconditionally — the same colour whether the line rises,
 *     falls or stays flat. Nothing here reads `danger`, `attention` or `accent`,
 *     and there is no branch anywhere in this file on the direction of the data.
 *     A weight going up is a fact; a weight going up in red against a goal line
 *     is a verdict, and this product does not give verdicts about bodies.
 *
 * ## The drawing is never the only carrier
 *
 * The line is `aria-hidden`: it is an illustration of numbers that are also
 * listed, in words, with their dates, underneath it — the same rule the week
 * strip follows. A reader who gets nothing from the picture loses nothing.
 *
 * ## Two points is the floor
 *
 * The caller does not render this with fewer, and it refuses anyway. One point
 * is a number, not a line, and drawing it with an axis is how a reader is shown
 * a trend that does not exist (`client-me`, "Too few measurements to draw
 * anything").
 */

const WIDTH = 320;
const HEIGHT = 96;
/** Room for the stroke and the dots, so nothing is clipped at the edges. */
const PAD = 8;

export function MeasurementChart({
  kind,
  entries,
  name,
}: {
  kind: MeasurementKind;
  entries: readonly Measurement[];
  /** The measurement's name, already resolved, for the accessible label. */
  name: string;
}) {
  if (entries.length < 2) return null;

  const values = entries.map((entry) => entry.value);
  const low = Math.min(...values);
  const high = Math.max(...values);
  /*
   * The band is the data's own, with a little room, and **never anchored to
   * zero**: a weight axis from 0 to 70 flattens four months of a person's life
   * into a straight line, and an axis that starts at a round number is a
   * reference value by another name. A flat run gets a band of its own so the
   * line sits in the middle rather than dividing by zero.
   */
  const span = high - low;
  const margin = span === 0 ? 1 : span * 0.15;
  const top = high + margin;
  const bottom = low - margin;

  const x = (index: number) => PAD + (index / (entries.length - 1)) * (WIDTH - PAD * 2);
  const y = (value: number) => PAD + ((top - value) / (top - bottom)) * (HEIGHT - PAD * 2);

  const points = entries.map((entry, index) => `${String(x(index))},${String(y(entry.value))}`);

  const first = entries[0];
  const last = entries[entries.length - 1];

  return (
    <div className="mt-3">
      {/*
        `text-primary` is set here and never conditionally. It is the deep green
        the palette already uses for the metric line (`src/index.css`), it
        measures 7.34:1 on the page ground, and it is the only colour on this
        chart in every state.
      */}
      <div className="text-primary">
        <svg
          viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
          width="100%"
          height={HEIGHT}
          role="img"
          aria-label={text('client.measurements.chartLabel', { name })}
          className="overflow-visible"
        >
          <polyline
            points={points.join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {entries.map((entry, index) => (
            <circle key={entry.id} cx={x(index)} cy={y(entry.value)} r={3} fill="currentColor" />
          ))}
        </svg>
      </div>

      {/*
        The ends of the line, in words: the first day and the last, and the
        lowest and highest number. Four facts, no axis furniture, and nothing
        that could be read as a threshold.
      */}
      <div aria-hidden="true" className="text-ink-muted mt-1 flex justify-between text-xs">
        <span>{first === undefined ? '' : formatDate(first.on)}</span>
        <span>
          {formatMeasurement(low, kind.unit)} – {formatMeasurement(high, kind.unit)}
        </span>
        <span>{last === undefined ? '' : formatDate(last.on)}</span>
      </div>
    </div>
  );
}
